import { SUPABASE_FUNCTIONS_URL } from '../config/constants';
import { supabase } from './supabaseClient';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  };
}

/**
 * Thrown when the caller expected early adopter pricing but the coupon is no
 * longer redeemable. The UI must re-render at the regular price and get a fresh
 * confirmation before sending anyone to Stripe — nobody should ever see the
 * launch price on our page and the regular price on the checkout screen.
 */
export class LaunchPricingUnavailableError extends Error {
  constructor() {
    super('Launch pricing is no longer available.');
    this.name = 'LaunchPricingUnavailableError';
  }
}

/**
 * Whether the launch offer can still be redeemed. Returns only a boolean —
 * the remaining redemption count is deliberately never exposed to the client.
 */
export async function getLaunchPricingAvailable(): Promise<boolean> {
  try {
    const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/launch-pricing-status`);
    if (!response.ok) return false;
    const { available } = await response.json();
    return available === true;
  } catch {
    // Never show the launch price on a failed lookup — the regular price is
    // always safe to display, the launch price is not.
    return false;
  }
}

/**
 * Starts a one-time Pro checkout. The server decides the price; the client
 * only says whether it displayed the early adopter price, so the server can
 * refuse (409) rather than charge an amount the user never saw.
 */
export async function createCheckoutSession(
  email: string,
  expectLaunchPricing = false
): Promise<string> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout-session`, {
    method: 'POST',
    headers,
    // The server builds the return URLs from its own allow-list.
    body: JSON.stringify({
      userEmail: email,
      expectLaunchPricing,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    if (error.code === 'launch_pricing_unavailable') {
      throw new LaunchPricingUnavailableError();
    }
    throw new Error(error.error || 'Failed to create checkout session');
  }

  const { url } = await response.json();
  return url;
}
